import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Search, Users, ChevronLeft, ChevronRight, Ticket, ShoppingBag, Calendar, User, Package } from 'lucide-react';
import producerProfilesApi from 'api/requests/producerProfiles';
import DashCollapse from 'components/_UI/DashCollapse';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pt-BR');
}

function formatDateTime(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR');
}

function formatPrice(value) {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/* Só celular (11 dígitos com o 9) tem WhatsApp garantido — mesma regra do
   EstablishmentDetailV2. Aceita o número já com o 55 na frente. */
function whatsappHref(raw) {
    let digits = String(raw || '').replace(/\D/g, '');
    if (digits.length === 13 && digits.startsWith('55')) digits = digits.slice(2);
    if (digits.length !== 11) return null;
    return `https://wa.me/55${digits}`;
}

function customerEvents(customer) {
    const names = [];
    (customer.purchases || []).forEach(p => {
        if (p.event?.name && !names.includes(p.event.name)) names.push(p.event.name);
    });
    return names;
}

/**
 * Sem `err.response`, a requisição nem chegou a um servidor pra responder —
 * é queda de conexão, não uma falha que o backend explique. Misturar as duas
 * sob a mesma mensagem genérica faz o produtor tentar de novo sem saber que
 * o problema é a própria internet dele.
 */
function getErrorMessage(err, fallback) {
    if (!err?.response) {
        return 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';
    }
    return err?.response?.data?.message || fallback;
}

const SORT_OPTIONS = [
    { value: 'recent', label: 'Compra mais recente' },
    { value: 'oldest', label: 'Compra mais antiga' },
    { value: 'most_tickets', label: 'Mais ingressos' },
    { value: 'most_orders', label: 'Mais pedidos' },
];

const PURCHASE_TYPE_OPTIONS = [
    { value: '', label: 'Ingressos e produtos' },
    { value: 'ticket', label: 'Só ingressos' },
    { value: 'product', label: 'Só produtos' },
];

export default function CustomersPage() {
    const [events, setEvents] = useState([]);

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [eventId, setEventId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [purchaseType, setPurchaseType] = useState('');
    const [sort, setSort] = useState('recent');

    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);

    const [detailCustomer, setDetailCustomer] = useState(null);
    const [detailRowKey, setDetailRowKey] = useState(null);
    const [eventsFilterError, setEventsFilterError] = useState(false);

    const debounceRef = useRef(null);

    // Clientes de quem vendeu ingresso/produto — uma função de `events.user_id`
    // — não de quem tem um perfil de produtor. As duas coisas são
    // independentes: o perfil é 100% opcional (formulário à parte, com CPF/CNPJ
    // obrigatório), e uma conta de estabelecimento cria evento e vende do
    // mesmo jeito sem nunca ter preenchido esse formulário. Por isso os
    // endpoints "me" abaixo resolvem direto pelo usuário logado, sem passar
    // por producer_profiles.
    useEffect(() => {
        fetchEventsForFilter();
    }, []);

    async function fetchEventsForFilter() {
        setEventsFilterError(false);
        try {
            const [{ data: activeData }, { data: expiredData }] = await Promise.all([
                producerProfilesApi.getMeEvents('active', 1, 50),
                producerProfilesApi.getMeEvents('expired', 1, 50),
            ]);
            const merged = [...(activeData?.data || []), ...(expiredData?.data || [])];
            setEvents(merged);
        } catch (err) {
            // Não bloqueia a lista de clientes — só o filtro por evento fica
            // indisponível — mas quem está tentando filtrar por evento
            // precisa saber que sumiu por erro, não porque não há eventos.
            setEventsFilterError(true);
        }
    }

    function buildParams(pageValue) {
        const params = { page: pageValue };
        if (search) params.search = search;
        if (eventId) params.event_id = eventId;
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        if (purchaseType) params.purchase_type = purchaseType;
        if (sort) params.sort = sort;
        return params;
    }

    async function fetchCustomers(pageValue = 1) {
        setLoading(true);
        setError(null);
        try {
            const { data } = await producerProfilesApi.getMeCustomers(buildParams(pageValue));
            setCustomers(data?.data || []);
            setPage(data?.meta?.current_page || pageValue);
            setLastPage(data?.meta?.last_page || 1);
            setTotal(data?.meta?.total ?? (data?.data || []).length);
            setDetailCustomer(null);
            setDetailRowKey(null);
        } catch (err) {
            setError(getErrorMessage(err, 'Erro ao carregar clientes.'));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchCustomers(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId, startDate, endDate, purchaseType, sort]);

    function handleSearchChange(e) {
        const value = e.target.value.slice(0, 100);
        setSearch(value);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchCustomers(1), 400);
    }

    function goToPage(nextPage) {
        if (nextPage < 1 || nextPage > lastPage) return;
        fetchCustomers(nextPage);
    }

    function toggleRow(customer, rowKey) {
        if (detailRowKey === rowKey) {
            closeDetail();
            return;
        }
        setDetailCustomer(customer);
        setDetailRowKey(rowKey);
    }

    function closeDetail() {
        setDetailCustomer(null);
        setDetailRowKey(null);
    }

    return (
        <Page>
            <Section>
                <SectionHeader>
                    <SectionHeaderText>
                        <SectionTitle>Clientes</SectionTitle>
                        {!loading && !error && (
                            <CustomerCount>{total} encontrado{total !== 1 ? 's' : ''}</CustomerCount>
                        )}
                    </SectionHeaderText>
                </SectionHeader>

                <FiltersWrapper>
                    <SearchWrapper>
                        <SearchIcon>
                            <Search size={16} color={colors.nightLight} />
                        </SearchIcon>
                        <SearchInput
                            type="text"
                            placeholder="Buscar por nome ou e-mail..."
                            value={search}
                            onChange={handleSearchChange}
                            maxLength={100}
                        />
                    </SearchWrapper>

                    <FilterRow>
                        <FilterField>
                            <FilterLabel>Evento</FilterLabel>
                            <FilterSelect value={eventId} onChange={e => setEventId(e.target.value)}>
                                <option value=''>Todos os eventos</option>
                                {events.map(ev => (
                                    <option key={ev.id || ev.slug} value={ev.id}>{ev.name}</option>
                                ))}
                            </FilterSelect>
                            {eventsFilterError && (
                                <FilterWarning>
                                    Não foi possível carregar os eventos.{' '}
                                    <FilterRetryLink type='button' onClick={fetchEventsForFilter}>
                                        Tentar novamente
                                    </FilterRetryLink>
                                </FilterWarning>
                            )}
                        </FilterField>

                        <FilterField>
                            <FilterLabel>Comprou</FilterLabel>
                            <FilterSelect value={purchaseType} onChange={e => setPurchaseType(e.target.value)}>
                                {PURCHASE_TYPE_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </FilterSelect>
                        </FilterField>

                        <FilterField>
                            <FilterLabel>De</FilterLabel>
                            <FilterDateInput type='date' value={startDate} onChange={e => setStartDate(e.target.value)} />
                        </FilterField>

                        <FilterField>
                            <FilterLabel>Até</FilterLabel>
                            <FilterDateInput type='date' value={endDate} onChange={e => setEndDate(e.target.value)} />
                        </FilterField>

                        <FilterField>
                            <FilterLabel>Ordenar por</FilterLabel>
                            <FilterSelect value={sort} onChange={e => setSort(e.target.value)}>
                                {SORT_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </FilterSelect>
                        </FilterField>
                    </FilterRow>
                </FiltersWrapper>

                {loading && <StateText>Carregando...</StateText>}

                {!loading && error && (
                    <EmptyState>
                        <EmptyTitle $error>Não foi possível carregar</EmptyTitle>
                        <EmptyHint>{error}</EmptyHint>
                        <ActionButton
                            type='button'
                            onClick={() => fetchCustomers(page)}
                        >
                            Tentar novamente
                        </ActionButton>
                    </EmptyState>
                )}

                {!loading && !error && customers.length === 0 && (
                    <EmptyState>
                        <EmptyTitle>Nenhum cliente encontrado</EmptyTitle>
                        <EmptyHint>
                            {search || eventId || purchaseType || startDate || endDate
                                ? 'Tente ajustar a busca ou os filtros.'
                                : 'Seus clientes aparecerão aqui assim que comprarem ingressos ou produtos dos seus eventos.'}
                        </EmptyHint>
                    </EmptyState>
                )}

                {!loading && !error && customers.length > 0 && (
                    <>
                        <TableWrapper>
                            <Table>
                                <thead>
                                    <tr>
                                        <Th>Nome</Th>
                                        <Th $hideOnMobile>Eventos</Th>
                                        <Th $hideOnMobile>Ingressos</Th>
                                        <Th $hideOnMobile>Produtos</Th>
                                        <Th $hideOnMobile>Última compra</Th>
                                        <Th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {customers.map((customer, index) => {
                                        const rowKey = customer.email || index;
                                        const expanded = detailRowKey === rowKey;
                                        const eventNames = customerEvents(customer);

                                        return (
                                            <React.Fragment key={rowKey}>
                                                <Tr $expanded={expanded} onClick={() => toggleRow(customer, rowKey)}>
                                                    <Td>
                                                        <CustomerName>{customer.name || '—'}</CustomerName>
                                                        <CustomerEmail>{customer.email}</CustomerEmail>
                                                        <MobileDetails>
                                                            <EventChipsRow>
                                                                {eventNames[0] && <EventChip>{eventNames[0]}</EventChip>}
                                                                {eventNames.length > 1 && <MoreChip>+{eventNames.length - 1} evento{eventNames.length - 1 !== 1 ? 's' : ''}</MoreChip>}
                                                            </EventChipsRow>
                                                            <span>{customer.tickets_count} ingresso{customer.tickets_count !== 1 ? 's' : ''} · {customer.products_count} produto{customer.products_count !== 1 ? 's' : ''}</span>
                                                        </MobileDetails>
                                                    </Td>
                                                    <Td $hideOnMobile>
                                                        <EventChipsRow>
                                                            {eventNames[0] && <EventChip>{eventNames[0]}</EventChip>}
                                                            {eventNames.length > 1 && <MoreChip>+{eventNames.length - 1}</MoreChip>}
                                                            {eventNames.length === 0 && '—'}
                                                        </EventChipsRow>
                                                    </Td>
                                                    <Td $hideOnMobile><CountBadge>{customer.tickets_count}</CountBadge></Td>
                                                    <Td $hideOnMobile><CountBadge>{customer.products_count}</CountBadge></Td>
                                                    <Td $hideOnMobile>{formatDate(customer.last_purchase_at)}</Td>
                                                    <Td align="right">
                                                        <ToggleButton
                                                            type="button"
                                                            $expanded={expanded}
                                                            aria-expanded={expanded}
                                                            onClick={(e) => { e.stopPropagation(); toggleRow(customer, rowKey); }}
                                                        >
                                                            <ChevronRight size={16} />
                                                        </ToggleButton>
                                                    </Td>
                                                </Tr>
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </TableWrapper>

                        <PaginationBar>
                            <PageInfo>Página {page} de {lastPage}</PageInfo>
                            <PaginationButtons>
                                <PageBtn onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                                    <ChevronLeft size={16} />
                                </PageBtn>
                                <PageBtn onClick={() => goToPage(page + 1)} disabled={page >= lastPage}>
                                    <ChevronRight size={16} />
                                </PageBtn>
                            </PaginationButtons>
                        </PaginationBar>
                    </>
                )}
            </Section>

            <DashCollapse visible={!!detailCustomer} onClose={closeDetail}>
                {detailCustomer && <CustomerDetailPanel customer={detailCustomer} />}
            </DashCollapse>
        </Page>
    );
}

function CustomerDetailPanel({ customer }) {
    const purchases = (customer.purchases || []).slice().reverse();
    const whatsapp = whatsappHref(customer.phone);

    return (
        <Panel>
            <Side>
                <SideTitle><User size={14} color={colors.night} />Cliente</SideTitle>

                <InfoRow>
                    <InfoLabel>E-mail</InfoLabel>
                    <InfoValue>{customer.email || '—'}</InfoValue>
                </InfoRow>
                <InfoRow>
                    <InfoLabel>Telefone</InfoLabel>
                    <PhoneValue>
                        {customer.phone || '—'}
                        {whatsapp && (
                            <WhatsappLink
                                href={whatsapp}
                                target='_blank'
                                rel='noopener noreferrer'
                                aria-label={`Chamar ${customer.name || 'cliente'} no WhatsApp`}
                                title='Chamar no WhatsApp'
                            >
                                <WhatsappIcon />
                            </WhatsappLink>
                        )}
                    </PhoneValue>
                </InfoRow>
                <InfoRow>
                    <InfoLabel>Documento</InfoLabel>
                    <InfoValue>{customer.document || '—'}</InfoValue>
                </InfoRow>
                <InfoRow $emphasis>
                    <InfoLabel>Primeira compra</InfoLabel>
                    <InfoValue>{formatDate(customer.first_purchase_at)}</InfoValue>
                </InfoRow>
                <InfoRow>
                    <InfoLabel>Última compra</InfoLabel>
                    <InfoValue>{formatDate(customer.last_purchase_at)}</InfoValue>
                </InfoRow>
            </Side>

            <Side>
                <SideTitle>
                    <Package size={14} color={colors.night} />
                    Pedidos
                    {customer.orders_count > 0 && <Count>{customer.orders_count}</Count>}
                </SideTitle>

                <PurchaseList>
                    {purchases.map(purchase => (
                        <PurchaseCard key={purchase.order_id}>
                            <PurchaseHeader>
                                <PurchaseEvent>
                                    <Calendar size={13} />
                                    {purchase.event?.name || 'Evento removido'}
                                </PurchaseEvent>
                                <PurchaseDate>{formatDateTime(purchase.created_at)}</PurchaseDate>
                            </PurchaseHeader>

                            <PurchaseItems>
                                {purchase.items.map((item, i) => (
                                    <PurchaseItemRow key={i}>
                                        {item.type === 'ticket'
                                            ? <Ticket size={13} color={panelAccent(colors.occasionalPurple)} />
                                            : <ShoppingBag size={13} color={colors.nightLight} />
                                        }
                                        <ItemName>{item.name}</ItemName>
                                        <ItemQty>x{item.quantity}</ItemQty>
                                        <ItemPrice>{formatPrice(item.price)}</ItemPrice>
                                    </PurchaseItemRow>
                                ))}
                            </PurchaseItems>
                        </PurchaseCard>
                    ))}
                </PurchaseList>
            </Side>
        </Panel>
    );
}

/* ─── Layout ─── */

const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Section = styled.div`
    background: #fff;
    border-radius: 14px;
    border: 1px solid ${colors.neutralDark};
    overflow: hidden;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const SectionHeaderText = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
`;

const CustomerCount = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
    font-weight: 500;
`;

/* ─── Filters ─── */

const FiltersWrapper = styled.div`
    padding: 14px 20px;
    border-bottom: 1px solid ${colors.neutralDark};
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SearchWrapper = styled.div`
    position: relative;
`;

const SearchIcon = styled.div`
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    display: flex;
    align-items: center;
`;

const SearchInput = styled.input`
    width: 100%;
    box-sizing: border-box;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 11px 16px 11px 38px;
    font-size: 14px;
    color: ${colors.nightDark};
    background: ${colors.neutralBackground};
    outline: none;
    transition: border-color 0.15s;

    &:focus {
        border-color: ${panelAccent(colors.occasionalPurple)};
        background: #fff;
    }

    &::placeholder {
        color: ${colors.nightLight};
    }
`;

const FilterRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const FilterField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 140px;
    flex: 1;
`;

const FilterWarning = styled.span`
    font-size: 11px;
    color: ${colors.danger};
`;

const FilterRetryLink = styled.button`
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-size: 11px;
    font-weight: 600;
    color: ${colors.danger};
    text-decoration: underline;
    cursor: pointer;
`;

const FilterLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const FilterSelect = styled.select`
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 13px;
    color: ${colors.nightDark};
    background: #fff;
    outline: none;

    &:focus {
        border-color: ${panelAccent(colors.occasionalPurple)};
    }
`;

const FilterDateInput = styled.input`
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 13px;
    color: ${colors.nightDark};
    background: #fff;
    outline: none;

    &:focus {
        border-color: ${panelAccent(colors.occasionalPurple)};
    }
`;

/* ─── States ─── */

const StateText = styled.p`
    text-align: center;
    color: ${({ $error }) => $error ? colors.danger : colors.nightLight};
    font-size: 14px;
    padding: 40px;
    margin: 0;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 48px 24px;
    text-align: center;
    gap: 8px;
`;

const EmptyTitle = styled.p`
    font-weight: 600;
    font-size: 15px;
    color: ${({ $error }) => $error ? colors.danger : colors.nightDark};
    margin: 0;
`;

const EmptyHint = styled.p`
    font-size: 13px;
    color: ${colors.nightLight};
    margin: 0;
`;

const ActionButton = styled.button`
    margin-top: 8px;
    padding: 10px 18px;
    border-radius: 10px;
    border: none;
    background: ${panelAccent(colors.occasionalPurple)};
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        opacity: 0.9;
    }
`;

/* ─── Table ─── */

const TableWrapper = styled.div`
    overflow-x: auto;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const Th = styled.th`
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 12px 20px;
    border-bottom: 1px solid ${colors.neutralDark};
    background: ${colors.neutralBackground};
    white-space: nowrap;

    ${({ $hideOnMobile }) => $hideOnMobile && `
        @media screen and (max-width: ${breakpoints.md}) {
            display: none;
        }
    `}
`;

const Td = styled.td`
    padding: 14px 20px;
    font-size: 14px;
    color: ${colors.nightDark};
    vertical-align: middle;
    text-align: ${({ align }) => align || 'left'};

    ${({ $hideOnMobile }) => $hideOnMobile && `
        @media screen and (max-width: ${breakpoints.md}) {
            display: none;
        }
    `}
`;

const Tr = styled.tr`
    cursor: pointer;
    border-bottom: 1px solid ${colors.neutralDark};
    background: ${({ $expanded }) => $expanded ? '#FAF6FF' : 'transparent'};
    transition: background 0.1s;

    &:hover {
        background: ${({ $expanded }) => $expanded ? '#FAF6FF' : colors.neutralBackground};
    }
`;

const ToggleButton = styled.button`
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: ${({ $expanded }) => $expanded ? panelAccent(colors.secondaryPurple) : colors.nightLight};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.15s ease, background 0.15s ease, color 0.15s ease;

    &:hover {
        background: ${colors.neutralBackground};
        color: ${colors.nightDark};
        transform: translateX(2px);
    }
`;

const CustomerName = styled.span`
    font-weight: 600;
    display: block;
`;

const CustomerEmail = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    display: block;
`;

const MobileDetails = styled.div`
    display: none;
    flex-direction: column;
    gap: 6px;
    margin-top: 6px;

    & > span {
        font-size: 12px;
        color: ${colors.nightLight};
    }

    @media screen and (max-width: ${breakpoints.md}) {
        display: flex;
    }
`;

const CountBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    color: ${panelAccent(colors.occasionalPurple)};
`;

/* ─── Event chips ─── */

const EventChipsRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
`;

const EventChip = styled.span`
    display: inline-flex;
    align-items: center;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 3px 10px;
    border-radius: 50px;
    font-size: 12px;
    font-weight: 600;
    background: ${colors.neutralBackground};
    border: 1px solid ${colors.neutralDark};
    color: ${colors.nightDark};
`;

const MoreChip = styled.span`
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    padding: 3px 10px;
    border-radius: 50px;
    font-size: 12px;
    font-weight: 600;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    color: ${panelAccent(colors.occasionalPurple)};
`;

/* ─── Pagination ─── */

const PaginationBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    border-top: 1px solid ${colors.neutralDark};
`;

const PageInfo = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
`;

const PaginationButtons = styled.div`
    display: flex;
    gap: 8px;
`;

const PageBtn = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1.5px solid ${colors.neutralDark};
    background: #fff;
    color: ${colors.nightDark};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: ${colors.neutralBackground};
    }
`;

/* ─── Detail panel (mirrors OrderDetailPanel) ─── */

const Panel = styled.div`
    display: grid;
    gap: 24px;
    padding: 18px;
    background: #FAFAFB;
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;

    @media screen and (min-width: ${breakpoints.smd}) {
        grid-template-columns: 1fr 1fr;
        gap: 32px;
        padding: 20px 22px;
    }
`;

const Side = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const SideTitle = styled.h4`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 8px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${colors.night};
`;

const Count = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 1px 7px;
    border-radius: 6px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    color: ${panelAccent(colors.occasionalPurple)};
    text-transform: none;
    letter-spacing: 0;
`;

const InfoRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    padding: 7px 0;

    ${({ $emphasis }) => $emphasis && `
        border-top: 1px solid ${colors.neutralDark};
        margin-top: 4px;
        padding-top: 10px;
    `}
`;

const InfoLabel = styled.span`
    font-size: 13px;
    color: ${colors.night};
`;

const InfoValue = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: ${colors.nightDark};
    text-align: right;
`;

const PhoneValue = styled(InfoValue)`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

/* Verde escuro do WhatsApp: o #25D366 da marca fica abaixo de 3:1 sobre o
   fundo claro do painel. */
const WhatsappLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    color: #128C7E;
    background: #25D3661F;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
        background: #25D36633;
        color: #075E54;
    }

    &:focus-visible {
        outline: 2px solid #128C7E;
        outline-offset: 2px;
    }
`;

function WhatsappIcon() {
    return (
        <svg width='16' height='16' viewBox='0 0 48 48' fill='currentColor' aria-hidden='true' focusable='false'>
            <path fillRule='evenodd' d='M23.993 0C10.763 0 0 10.765 0 24a23.824 23.824 0 0 0 4.57 14.067l-2.99 8.917 9.224-2.948A23.81 23.81 0 0 0 24.007 48C37.237 48 48 37.234 48 24S37.238 0 24.007 0h-.014Zm-6.7 12.19c-.466-1.114-.818-1.156-1.523-1.185a13.55 13.55 0 0 0-.804-.027c-.918 0-1.877.268-2.455.86-.705.72-2.454 2.398-2.454 5.841s2.51 6.773 2.849 7.239c.353.465 4.895 7.632 11.947 10.553 5.515 2.286 7.152 2.074 8.407 1.806 1.834-.395 4.133-1.750 4.711-3.386.579-1.637.579-3.034.41-3.330-.17-.296-.636-.465-1.340-.818-.706-.353-4.134-2.046-4.783-2.272-.634-.24-1.240-.155-1.720.522-.677.946-1.340 1.905-1.876 2.483-.423.452-1.115.509-1.693.268-.776-.324-2.948-1.086-5.628-3.470-2.074-1.849-3.484-4.148-3.893-4.840-.41-.705-.042-1.114.282-1.495.353-.438.691-.748 1.044-1.157.352-.41.55-.621.776-1.100.24-.466.07-.946-.1-1.300-.168-.352-1.579-3.795-2.157-5.191Z' />
        </svg>
    );
}

const PurchaseList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const PurchaseCard = styled.div`
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
`;

const PurchaseHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 14px;
    background: ${colors.neutralBackground};
`;

const PurchaseEvent = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const PurchaseDate = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const PurchaseItems = styled.div`
    display: flex;
    flex-direction: column;
`;

const PurchaseItemRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border-top: 1px solid ${colors.neutralDark};
    font-size: 13px;
`;

const ItemName = styled.span`
    flex: 1;
    color: ${colors.nightDark};
`;

const ItemQty = styled.span`
    color: ${colors.nightLight};
`;

const ItemPrice = styled.span`
    font-weight: 600;
    color: ${colors.nightDark};
`;
